import { ILoggedCustomer, IPurchaseCategory } from "api/types";
import { ApolloClient } from "apollo-boost";
import { AppStateContext } from "AppState";
import Button from "components/common/Button/Button";
import Divider from "components/common/Divider";
import NumPad, { MediumCenteredNumPad } from "components/common/NumPad";
import PhoneNumberInput from "components/common/PhoneNumberInput/PhoneNumberInput";
import PinInput from "components/common/PinInput";
import RightLeftLayout from "components/common/RightLeftLayout/RightLeftLayout";
import Text from "components/common/Text/Text";
import { CustomerDataContext } from "components/Gates/CustomerDataGate";
import { TabletDataContext } from "components/Gates/TabletDataGate";
import { TabletStartupDataContext } from "components/Gates/TabletStartupGate";
import PromoCodeInput, { IPromoCode } from "components/PromoCode";
import { History } from "history";
import * as React from "react";
import { ApolloConsumer } from "react-apollo";
import { Col, Row } from "react-grid-system";
import Loader from "react-loader-spinner";
import { Routes } from "router";
import strings from "static/strings.js";
import theme from "themes/simple-theme";
import {
  recordPurchase,
  validatePromoCode,
  validateTenantUserPin
} from "utils/apis";
import { load, store } from "utils/ConfigLoader";
import getDetailedGraphqlError from "utils/errorHandling";
import transformError from "utils/errorHandling";
import titleCase from "utils/format";
import Checkbox from "../../components/common/Checkbox/Checkbox";
import { StyledPurchaseCategoryCheckboxAndLabel } from "./Budtender.style";
import LabelGroupWrapper from "./LabelGroupWrapper";

interface IProps {
  history: History;
  whiteLabelMode?: boolean;
  locationName: string;
  purchaseCategories: [IPurchaseCategory];
}

interface IState {
  categories: Array<{
    name: string;
    checked: boolean;
    id: string;
  }>;
  currentControl: string;
  discount: number;
  totalPrice: string;
  budtenderId: string;
  promoCode: IPromoCode;
  goodPin?: boolean;
  wrongPin?: boolean;
  error?: string;
  purchasing?: boolean;
}

class Budtender extends React.Component<
  IProps & {
    client: ApolloClient<any>;
    loggedCustomer: ILoggedCustomer | undefined;
  },
  IState
> {
  constructor(
    props: IProps & {
      client: ApolloClient<any>;
      loggedCustomer: ILoggedCustomer | undefined;
    }
  ) {
    super(props);
    this.state = {
      categories: props.purchaseCategories.map(x => ({
        id: x.id,
        name: x.name,
        checked: false
      })),
      currentControl: "totalPrice",
      discount: 0,
      totalPrice: "$0",
      budtenderId: "",
      promoCode: {
        code: ""
      }
    };
  }

  public render() {
    const { totalPrice } = this.state;
    const disabled = !this.state.goodPin || !totalPrice;
    return (
      <RightLeftLayout
        title={this.props.locationName}
        titleSecondary="Confirm Purchase"
        closeButtonHandler
        leftPanel={this.leftPanel(this.state, disabled)}
        rightPanel={
          <MediumCenteredNumPad>
            <NumPad onTap={this.onTap} />
          </MediumCenteredNumPad>
        }
      />
    );
  }

  public componentDidMount() {
    const code = load("purchase.promoCode");
    if (code) {
      this.setState({
        promoCode: {
          code
        }
      });
      this.validatePromoCode(code);
    }
  }

  private leftPanel = (state: IState, disabled: boolean) => {
    const {
      promoCode,
      currentControl,
      categories,
      totalPrice,
      error,
      budtenderId,
      purchasing
    } = state;
    const pinGen = "﹡﹡﹡﹡".slice(0, (budtenderId || "").length);
    return (
      <React.Fragment>
        <Row style={{ flex: 1, marginTop: 20 }}>
          <Col xs={7}>
            {categories.length > 0 && (
              <Text alternate>Categories (Optional)</Text>
            )}
            <Row>
              {categories.map(category => (
                <Col xs={6} key={category.id}>
                  <StyledPurchaseCategoryCheckboxAndLabel
                    checked={category.checked}
                    label={category.name}
                    onClick={this.switchCategory(category.name)}
                  />
                </Col>
              ))}
            </Row>
          </Col>
          <Col
            xs={5}
            style={{
              flexDirection: "column",
              justifyContent: "space-between",
              display: "flex"
            }}
          >
            <div>
              <PromoCodeInput
                promoCode={promoCode}
                onFocus={this.onFocus("promoCode")}
                focused={currentControl === "promoCode"}
                title="Promo Code (Optional)"
                budtenderFacing
              />
            </div>
            <div>
              <div>
                <Text alternate>Total Price</Text>
              </div>
              <div style={{ marginTop: 10 }}>
                <PhoneNumberInput
                  value={totalPrice}
                  fullWidth
                  readOnly
                  focused={currentControl === "totalPrice"}
                  onFocus={this.onFocus("totalPrice")}
                />
              </div>
              <LabelGroupWrapper>
                <Text h5 alternate error>
                  {error}
                </Text>
              </LabelGroupWrapper>
              {/* <LabelGroupWrapper>
                    <Text h5>DISCOUNT:</Text>
                    <Text h5>{discount}%</Text>
                  </LabelGroupWrapper>
                  <LabelGroupWrapper>
                    <Text h5>FINAL TOTAL:</Text>
                    <Text h5>
                      ${parseFloat(totalPrice.slice(1) || "0") *
                        (100 - discount) /
                        100}
                    </Text>
                  </LabelGroupWrapper> */}
            </div>
          </Col>
        </Row>
        <div style={{ width: "100%", marginTop: "5rem" }}>
          <Divider transparent />
          <Row
            style={{
              alignItems: "flex-end",
              width: "100%",
              justifyContent: "flex-end"
            }}
          >
            <div>
              <div>
                <Text alternate>{titleCase(strings.cashier)} ID</Text>
              </div>
              <div style={{ marginTop: 10 }}>
                <PinInput
                  pin={pinGen}
                  goodPin={this.state.goodPin}
                  wrongPin={this.state.wrongPin}
                  onClick={this.onFocus("budtenderId")}
                  focused={currentControl === "budtenderId"}
                  small
                />
              </div>
            </div>
            <div style={{ marginLeft: 70 }}>
              {purchasing ? (
                <div
                  style={{
                    padding: "0px 62px"
                  }}
                >
                  <Loader
                    type="ThreeDots"
                    color={theme.color.primary}
                    height={60}
                    width={80}
                  />
                </div>
              ) : (
                <Button
                  onClick={this.recordPurchase}
                  primary
                  disabled={disabled}
                >
                  Confirm Transaction
                </Button>
              )}
            </div>
          </Row>
        </div>
      </React.Fragment>
    );
  };
  private validatePromoCode = async (code: string) => {
    const authToken = this.props.loggedCustomer
      ? this.props.loggedCustomer.authToken
      : "";
    try {
      const result = await validatePromoCode(
        this.props.client,
        authToken,
        code
      );
      if (result) {
        this.setState({
          promoCode: {
            code,
            valid: true,
            invalid: false,
            name: result.name,
            details: result.details
          }
        });
      } else {
        this.setState({
          promoCode: {
            code,
            invalid: true,
            details: "This Code is not Valid"
          }
        });
      }
    } catch (e) {
      console.error(e);
      let errorName = "This Code is not Valid";
      if (e.graphQLErrors && e.graphQLErrors.length > 0) {
        errorName = getDetailedGraphqlError(e.graphQLErrors);
      }

      this.setState({
        promoCode: {
          code,
          invalid: true,
          details: errorName
        }
      });
    }
  };
  private switchCategory = (name: string) => () => {
    const { categories } = this.state;
    const newCategories = categories.slice();
    const categoryIndex = newCategories.findIndex(
      (category: any) => category.name === name
    );
    newCategories[categoryIndex].checked = !newCategories[categoryIndex]
      .checked;
    this.setState({ categories: newCategories });
  };

  private onTap = (value: string) => {
    const { currentControl } = this.state;
    if (!currentControl) {
      return;
    }
    const currentValue = this.state[currentControl];
    let newValue = currentValue;
    switch (currentControl) {
      case "totalPrice":
        if (value === "back" && currentValue.length > 1) {
          newValue = currentValue.slice(0, currentValue.length - 1);
          if (newValue.length === 1) {
            newValue = "$0";
          }
        } else if (value !== "back") {
          if (currentValue === "$0") {
            newValue = `$${value}`;
          } else {
            newValue = `${currentValue}${value}`;
          }
        }
        this.setState({ totalPrice: newValue, error: "" });
        break;
      case "budtenderId":
        if (value === "back" && currentValue.length > 0) {
          newValue = currentValue.slice(0, currentValue.length - 1);
        } else if (value !== "back" && currentValue.length < 4) {
          newValue = `${currentValue}${value}`;
        }

        if (newValue.length === 4) {
          this.validateBudtenderPin(newValue);
        }

        this.setState({
          budtenderId: newValue,
          wrongPin: false,
          goodPin: false,
          error: ""
        });
        break;
      case "promoCode":
        const { code } = currentValue;
        newValue = code;
        if (value === "back" && code.length > 0) {
          newValue = code.slice(0, code.length - 1);
        } else if (value !== "back" && code.length < 6) {
          newValue = `${code}${value}`;
        }
        if (newValue.length >= 6) {
          this.validatePromoCode(newValue);
        }
        // @ts-ignore
        this.setState({ promoCode: { code: newValue, error: "" } });
        break;
    }
  };

  private onFocus = (control: string) => () => {
    if (control === "totalPrice" && this.state.totalPrice === "$0") {
      this.setState({ currentControl: control, totalPrice: "$" });
      return;
    } else if (control !== "totalPrice" && this.state.totalPrice === "$") {
      this.setState({ currentControl: control, totalPrice: "$0" });
      return;
    }
    this.setState({ currentControl: control });
  };

  private recordPurchase = async () => {
    const { budtenderId, categories, totalPrice, purchasing } = this.state;
    if (purchasing) {
      return;
    }
    const selectedCategories = categories.filter(
      (category: any) => category.checked
    );
    const totalPriceInt = parseInt(totalPrice.slice(1), 10);
    if (totalPriceInt <= 0) {
      this.setState({
        error: "Please enter a purchase price greater than 0."
      });
      return;
    }
    const authToken = this.props.loggedCustomer
      ? this.props.loggedCustomer.authToken
      : "";

    try {
      this.setState({
        purchasing: true
      });
      const purchaseResult: any = await recordPurchase(
        this.props.client,
        authToken,
        budtenderId,
        totalPriceInt,
        selectedCategories.map(category => category.id),
        this.state.promoCode.code
      );
      if (purchaseResult) {
        store("purchase.pointsEarned", purchaseResult.pointsEarned);
        store("purchase.totalPrice", totalPrice);
        store("purchase.categoryList", selectedCategories);
        this.setState({
          goodPin: true,
          wrongPin: false,
          error: "",
          purchasing: false
        });
        this.props.history.push(Routes.PurchaseComplete);
      } else {
        this.setState({
          wrongPin: true,
          goodPin: false,
          error: "",
          purchasing: false
        });
      }
    } catch (err) {
      this.setState({ purchasing: false });
      console.error("couldn't record purchase", err);
    }
  };

  private validateBudtenderPin = async (pin: string) => {
    try {
      const pinValidity: any = await validateTenantUserPin(
        this.props.client,
        pin
      );
      if (!pinValidity || pinValidity.invalidityCause) {
        this.setState({ wrongPin: true });
      } else {
        this.setState({ goodPin: true });
      }
    } catch (err) {
      console.error(`couldn't validate ${strings.cashier} pin`, err);
    }
  };
}

export default (props: IProps) => (
  <ApolloConsumer>
    {client => (
      <CustomerDataContext.Consumer>
        {loggedCustomer => (
          <TabletStartupDataContext.Consumer>
            {purchaseCategories => (
              <TabletDataContext.Consumer>
                {tabletData =>
                  tabletData && (
                    <AppStateContext.Consumer>
                      {([appState]) => (
                        <Budtender
                          whiteLabelMode={tabletData.whiteLabelMode}
                          client={client}
                          loggedCustomer={loggedCustomer}
                          purchaseCategories={purchaseCategories}
                          locationName={appState.locationName}
                          {...props}
                        />
                      )}
                    </AppStateContext.Consumer>
                  )
                }
              </TabletDataContext.Consumer>
            )}
          </TabletStartupDataContext.Consumer>
        )}
      </CustomerDataContext.Consumer>
    )}
  </ApolloConsumer>
);



// WEBPACK FOOTER //
// ./src/screens/Budtender/index.tsx